import InfoOutlined from '@mui/icons-material/InfoOutlined';
import SearchIcon from '@mui/icons-material/Search';
import {
  Alert,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputAdornment,
  InputLabel,
  List,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { BulkOperationError } from 'graphql/generated/graphqlTypes';
import useTranslation from 'hooks/useTranslation/useTranslation';
import React, { useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import Loader from 'v2/components/Loader/Loader';
import ModalTemplate from 'v2/components/Modal/ModalTemplate';
import useModalContext from 'v2/components/Modal/useModalContext';
import useSnackbarContext from 'v2/components/Snackbar/useSnackbarContext';
import useBulkUserActions from 'v2/hooks/useBulkUserActions/useBulkUserActions';
import { useSearch } from 'v2/hooks/useSearch/useSearch';
import useVisitorsGroups from 'v2/hooks/useVisitorsGroups/useVisitorsGroups';
import { canManageUsersInVisitorGroup } from 'v2/utils/visitorGroupOrigin';
import { getSelectedUserDescription } from '../../components/selectedUsersUtils';
import { SelectedUser } from '../BulkUserActionFailuresModal/bulkUserActionErrorsUtils';
import BulkUserActionFailuresModal from '../BulkUserActionFailuresModal/BulkUserActionFailuresModal';
import { getSelectableGroupIds, getSelectAllState, toggleVisibleSelectableGroupIds } from './addUsersToGroupsUtils';
import GroupListItem from './GroupListItem';

const FORM_ID = 'add-users-to-groups-form';
const GROUP_LIST_MAX_HEIGHT = 384;
const searchKeys = ['name'];

interface FormValues {
  selectedGroupIds: string[];
}

interface Props {
  selectedUsers: SelectedUser[];
  onSuccess: () => void;
}

type SortOrder = 'name_asc' | 'name_desc';

const AddUsersToGroupsModal: React.FC<Props> = ({ selectedUsers, onSuccess }) => {
  const { translation } = useTranslation();
  const { closeModal } = useModalContext();
  const { showSnackbar } = useSnackbarContext();

  const [search, setSearch] = useState('');
  const [sortOrder, setSortOrder] = useState<SortOrder>('name_asc');

  const [errors, setErrors] = useState<BulkOperationError[]>([]);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [submittedGroupIds, setSubmittedGroupIds] = useState<string[]>([]);

  const { allVisitorGroups, loadingAllVisitorGroups } = useVisitorsGroups({
    handleFetchError: setErrorMessage
  });

  const { bulkAddVisitorsToVisitorGroups, bulkAddVisitorsToVisitorGroupsLoading } = useBulkUserActions({
    translation,
    handleFetchError: setErrorMessage
  });

  const {
    control,
    handleSubmit,
    formState: { isSubmitting }
  } = useForm<FormValues>({
    mode: 'onTouched',
    defaultValues: { selectedGroupIds: [] }
  });

  const manageableVisitorGroups = useMemo(
    () => allVisitorGroups.filter(canManageUsersInVisitorGroup),
    [allVisitorGroups]
  );

  const filteredVisitorGroups = useSearch({ data: manageableVisitorGroups, search, keys: searchKeys });

  const visibleVisitorGroups = useMemo(() => {
    const collator = new Intl.Collator(undefined, { sensitivity: 'base', numeric: true });

    const sorted = [...filteredVisitorGroups].sort((first, second) =>
      collator.compare(first.name ?? '', second.name ?? '')
    );

    return sortOrder === 'name_desc' ? sorted.reverse() : sorted;
  }, [filteredVisitorGroups, sortOrder]);

  const handleSuccess = () => {
    showSnackbar(translation.users_added_to_groups, { severity: 'success' });
    onSuccess();
    closeModal();
  };

  const handleResult = (payload: { errors: BulkOperationError[] }) => {
    if (payload.errors.length > 0) {
      setErrors(payload.errors);
      return;
    }

    handleSuccess();
  };

  const onSubmit = async ({ selectedGroupIds }: FormValues) => {
    setSubmittedGroupIds(selectedGroupIds);
    setErrorMessage(undefined);
    const payload = await bulkAddVisitorsToVisitorGroups({
      visitorIds: selectedUsers.map(({ id }) => id),
      visitorGroupIds: selectedGroupIds
    });

    if (payload) handleResult(payload);
  };

  const handleRetry = async (failedVisitorIds: string[]) => {
    setErrorMessage(undefined);
    const payload = await bulkAddVisitorsToVisitorGroups({
      visitorIds: failedVisitorIds,
      visitorGroupIds: submittedGroupIds
    });

    if (payload) handleResult(payload);
  };

  if (errors.length > 0) {
    return (
      <BulkUserActionFailuresModal
        errors={errors}
        selectedUsers={selectedUsers}
        title={translation.bulk_action_failures_title}
        loading={bulkAddVisitorsToVisitorGroupsLoading}
        errorMessage={errorMessage}
        onRetry={handleRetry}
      />
    );
  }

  return (
    <ModalTemplate
      title={translation.add_to_groups}
      primaryAction={{
        id: 'apply-add-users-to-groups-button',
        label: translation.apply,
        form: FORM_ID,
        loading: isSubmitting || bulkAddVisitorsToVisitorGroupsLoading,
        disabled: loadingAllVisitorGroups || isSubmitting || bulkAddVisitorsToVisitorGroupsLoading
      }}
      secondaryAction={{ id: 'cancel-add-users-to-groups-button', label: translation.cancel }}
    >
      {loadingAllVisitorGroups ? (
        <Stack alignItems="center" justifyContent="center" height="100%">
          <Loader />
        </Stack>
      ) : (
        <Stack component="form" id={FORM_ID} onSubmit={handleSubmit(onSubmit)} noValidate gap={3}>
          {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
          <Typography>{getSelectedUserDescription(selectedUsers.length, translation.add_to_groups_info)}</Typography>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 8 }}>
              <TextField
                id="add-users-to-groups-search-input"
                placeholder={translation.search_user_groups}
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                fullWidth
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    )
                  }
                }}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 4 }}>
              <FormControl fullWidth>
                <InputLabel id="add-users-to-groups-sort-label">{translation.sort}</InputLabel>
                <Select
                  labelId="add-users-to-groups-sort-label"
                  id="add-users-to-groups-sort-select"
                  label={translation.sort}
                  value={sortOrder}
                  onChange={(event) => setSortOrder(event.target.value as SortOrder)}
                >
                  <MenuItem value="name_asc">{translation.a_z}</MenuItem>
                  <MenuItem value="name_desc">{translation.z_a}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Controller
            name="selectedGroupIds"
            control={control}
            rules={{ validate: (value) => value.length > 0 || translation.select_at_least_one_group }}
            render={({ field, fieldState }) => {
              const selectedUserIds = selectedUsers.map(({ id }) => id);
              const visibleSelectableGroupIds = getSelectableGroupIds(visibleVisitorGroups, selectedUserIds);
              const { checked, indeterminate } = getSelectAllState(visibleSelectableGroupIds, field.value);

              return (
                <Stack gap={1}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Stack direction="row" alignItems="center" gap={0.5}>
                      <Typography variant="subtitle1">{translation.user_groups}</Typography>
                      <Tooltip title={translation.entra_groups_excluded_from_bulk_add}>
                        <InfoOutlined color="action" fontSize="small" />
                      </Tooltip>
                    </Stack>

                    <FormControlLabel
                      label={translation.select_all}
                      labelPlacement="start"
                      sx={{ m: 0, gap: 0.5 }}
                      control={
                        <Checkbox
                          id="select-all-user-groups-checkbox"
                          checked={checked}
                          indeterminate={indeterminate}
                          onChange={() =>
                            field.onChange(toggleVisibleSelectableGroupIds(field.value, visibleSelectableGroupIds))
                          }
                        />
                      }
                    />
                  </Stack>

                  <List
                    disablePadding
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.5,
                      maxHeight: GROUP_LIST_MAX_HEIGHT,
                      overflowY: 'auto'
                    }}
                  >
                    {visibleVisitorGroups.map((visitorGroup) => (
                      <GroupListItem
                        key={visitorGroup.id}
                        visitorGroup={visitorGroup}
                        selectedUserIds={selectedUserIds}
                        checked={field.value.includes(visitorGroup.id)}
                        onChange={(isChecked) =>
                          field.onChange(
                            isChecked
                              ? [...field.value, visitorGroup.id]
                              : field.value.filter((groupId) => groupId !== visitorGroup.id)
                          )
                        }
                      />
                    ))}
                  </List>

                  {fieldState.error && <FormHelperText error>{fieldState.error.message}</FormHelperText>}
                </Stack>
              );
            }}
          />
        </Stack>
      )}
    </ModalTemplate>
  );
};

export default AddUsersToGroupsModal;
